import { useWorkspaceScope } from './useWorkspaceScope';
import { baseApi } from '@shared/store/api/baseApi';

export type WorkspaceAction = {
  kind:
    | 'campaign-summary'
    | 'campaign-start'
    | 'campaign-stop'
    | 'campaign-rename'
    | 'webhook-dispatch';
  campaignId: string;
  callId?: string;
  title?: string;
};
export type MissionLimits = {
  credits?: string | null;
  tools?: number | null;
  seconds?: number | null;
};
export type RuntimeBudgetUsage = {
  id: string;
  state: 'prepared' | 'running' | 'draining' | 'settling' | 'completed' | 'review';
  financialState: 'known' | 'unavailable' | 'review';
  limits: MissionLimits;
  creditScale: number | null;
  limitUnits: string | null;
  spentUnits: string | null;
  accruedUnits: string | null;
  heldUnits: string | null;
  overrunUnits: string | null;
  toolInvocations: number;
  activeCalls: number;
  pendingCalls: number;
  unsettledCalls: number;
  unknownDispatches: number;
  pendingDispatches: number;
  commandsFinished: boolean;
  exhaustionReason: string | null;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
};
export type MissionSpec = {
  limits?: MissionLimits;
  name: string;
  brief: string;
  trigger: {
    kind: 'manual' | 'schedule' | 'call-ended';
    cron?: string;
    timezone?: string;
    filters?: MissionEventFilters;
  };
  actions: WorkspaceAction[];
};
export type MissionEventField =
  | 'campaignId'
  | 'type'
  | 'direction'
  | 'status'
  | 'endedReason'
  | 'agentId'
  | 'squadId';
export type MissionEventFilters = {
  mode: 'all' | 'any';
  conditions: {
    field: MissionEventField;
    operator: 'equals' | 'not-equals' | 'contains' | 'exists';
    value: string;
  }[];
};
export type Mission = {
  id: string;
  draft: MissionSpec;
  revision: number;
  paused: boolean;
  active_version_id: string | null;
  next_due_at: string | null;
  created_at: string;
};
export type MissionConfigurationCheck = {
  nextOccurrence: string | null;
  actions: {
    kind: WorkspaceAction['kind'];
    campaignId: string;
    status: 'checked' | 'blocked' | 'deferred';
    message: string;
  }[];
};
export type MissionSavedCheck = {
  id: string;
  missionId: string;
  versionId: string;
  versionNumber: number;
  actorId: string;
  retryOf: string | null;
  createdAt: string;
  result: MissionConfigurationCheck;
};
export type MissionVersion = { id: string; number: number; spec: MissionSpec; created_at: string };
export type WorkspaceOperation = {
  id: string;
  mission_id: string | null;
  version_id: string | null;
  state:
    | 'prepared'
    | 'planning'
    | 'queued'
    | 'running'
    | 'succeeded'
    | 'failed'
    | 'review'
    | 'canceled';
  runtimeBudget?: RuntimeBudgetUsage;
  actions: WorkspaceAction[];
  snapshots: unknown[];
  result: unknown[];
  error_code: string | null;
  created_at: string;
  expires_at: string | null;
};
export type AssistantMessage = { role: 'user' | 'assistant'; content: string };
export type WorkspaceJobUsage = {
  profileId: string;
  agentCount: number;
  campaignCount: number;
};
export type WorkspaceJobAssignments = {
  agents: {
    id: string;
    name: string;
    campaigns: { id: string; title: string; status: string }[];
  }[];
  next: string | null;
  total: number;
};
const root = '/v2/workspace';
const api = baseApi.enhanceEndpoints({ addTagTypes: ['Workspace'] }).injectEndpoints({
  endpoints: (build) => ({
    previewWorkspaceMissionEvent: build.mutation<
      { metadata: Partial<Record<MissionEventField, string | null>>; matches: boolean },
      { spec: MissionSpec; callId: string }
    >({
      query: (body) => ({ url: `${root}/missions/preview-event`, method: 'POST', body }),
    }),
    workspaceMissionChecks: build.query<
      { items: MissionSavedCheck[]; next: string | null },
      { scope: string; id: string; before?: string }
    >({
      query: ({ id, before }) => ({ url: `${root}/missions/${id}/checks`, params: { before } }),
      providesTags: ['Workspace'],
    }),
    recordWorkspaceMissionCheck: build.mutation<
      MissionSavedCheck,
      { id: string; versionId: string; retryOf?: string }
    >({
      query: ({ id, ...body }) => ({
        url: `${root}/missions/${id}/check-configuration`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Workspace'],
    }),
    workspaceJobUsage: build.query<WorkspaceJobUsage[], { scope: string }>({
      query: () => `${root}/job-usage`,
      providesTags: ['Workspace', 'Campaign'],
    }),
    workspaceJobAssignments: build.query<
      WorkspaceJobAssignments,
      { scope: string; id: string; after?: string }
    >({
      query: ({ id, after }) => ({
        url: `${root}/jobs/${id}/usage`,
        params: { after: after || undefined },
      }),
      providesTags: ['Workspace', 'Campaign'],
    }),
    checkWorkspaceMissionConfiguration: build.mutation<
      MissionConfigurationCheck,
      { spec: MissionSpec }
    >({
      query: (body) => ({ url: `${root}/missions/check-configuration`, method: 'POST', body }),
    }),
    workspaceMissions: build.query<Mission[], { scope: string; after?: string }>({
      query: ({ after }) => ({ url: `${root}/missions`, params: { after: after || undefined } }),
      providesTags: ['Workspace'],
    }),
    workspaceMission: build.query<
      { mission: Mission; versions: MissionVersion[] },
      { scope: string; id: string }
    >({
      query: ({ id }) => `${root}/missions/${id}`,
      providesTags: ['Workspace'],
    }),
    createWorkspaceMission: build.mutation<Mission, { id: string; spec: MissionSpec }>({
      query: (body) => ({ url: `${root}/missions`, method: 'POST', body }),
      invalidatesTags: ['Workspace'],
    }),
    saveWorkspaceMission: build.mutation<
      Mission,
      { id: string; revision: number; spec: MissionSpec }
    >({
      query: ({ id, ...body }) => ({ url: `${root}/missions/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['Workspace'],
    }),
    publishWorkspaceMission: build.mutation<Mission, { id: string; revision: number }>({
      query: ({ id, ...body }) => ({ url: `${root}/missions/${id}/publish`, method: 'POST', body }),
      invalidatesTags: ['Workspace'],
    }),
    pauseWorkspaceMission: build.mutation<
      Mission,
      { id: string; revision: number; paused: boolean }
    >({
      query: ({ id, ...body }) => ({ url: `${root}/missions/${id}/pause`, method: 'POST', body }),
      invalidatesTags: ['Workspace'],
    }),
    archiveWorkspaceMission: build.mutation<void, { id: string; revision: number }>({
      query: ({ id, ...body }) => ({ url: `${root}/missions/${id}`, method: 'DELETE', body }),
      invalidatesTags: ['Workspace'],
    }),
    runWorkspaceMission: build.mutation<WorkspaceOperation, { id: string; requestId: string }>({
      query: ({ id, ...body }) => ({ url: `${root}/missions/${id}/run`, method: 'POST', body }),
      invalidatesTags: ['Workspace'],
    }),
    workspaceOperations: build.query<
      WorkspaceOperation[],
      { scope: string; missionId?: string; before?: string }
    >({
      query: ({ scope: _scope, ...params }) => ({ url: `${root}/operations`, params }),
      providesTags: ['Workspace'],
    }),
    confirmWorkspaceOperation: build.mutation<WorkspaceOperation, { id: string; cancel?: boolean }>(
      {
        query: ({ id, cancel }) => ({
          url: `${root}/operations/${id}/${cancel ? 'cancel' : 'confirm'}`,
          method: 'POST',
        }),
        invalidatesTags: ['Workspace', 'Campaign', 'CompanyOverview'],
      },
    ),
    acknowledgeWorkspaceReview: build.mutation<
      RuntimeBudgetUsage,
      { id: string; requestId: string }
    >({
      query: ({ id, requestId }) => ({
        url: `${root}/operations/${id}/acknowledge-review`,
        method: 'POST',
        body: { requestId },
      }),
      invalidatesTags: ['Workspace', 'Campaign'],
    }),
    workspaceModels: build.query<
      { id: string; title: string; models: { id: string; title: string }[] }[],
      { scope: string }
    >({ query: () => `${root}/models` }),
    askWorkspace: build.mutation<
      { answer: string; operation: WorkspaceOperation | null },
      {
        providerId: string;
        modelId: string;
        campaignId: string;
        requestId: string;
        messages: AssistantMessage[];
      }
    >({
      query: (body) => ({ url: `${root}/ask`, method: 'POST', body }),
      invalidatesTags: ['Workspace'],
    }),
  }),
});
export const {
  useAcknowledgeWorkspaceReviewMutation,
  usePreviewWorkspaceMissionEventMutation,
  useRecordWorkspaceMissionCheckMutation,
  useWorkspaceJobUsageQuery,
  useWorkspaceJobAssignmentsQuery,
  useCheckWorkspaceMissionConfigurationMutation,
  useCreateWorkspaceMissionMutation,
  useSaveWorkspaceMissionMutation,
  usePublishWorkspaceMissionMutation,
  usePauseWorkspaceMissionMutation,
  useArchiveWorkspaceMissionMutation,
  useRunWorkspaceMissionMutation,
  useConfirmWorkspaceOperationMutation,
  useAskWorkspaceMutation,
} = api;

type QueryOptions = {
  skip?: boolean;
  pollingInterval?: number;
  skipPollingIfUnfocused?: boolean;
  refetchOnMountOrArgChange?: boolean;
};
export const useWorkspaceMissionsQuery = (after: string | void, options?: QueryOptions) =>
  api.useWorkspaceMissionsQuery({ scope: useWorkspaceScope(), after: after || undefined }, options);
export const useWorkspaceMissionQuery = (id: string, options?: QueryOptions) =>
  api.useWorkspaceMissionQuery({ scope: useWorkspaceScope(), id }, options);
export const useWorkspaceOperationsQuery = (
  params: { missionId?: string; before?: string },
  options?: QueryOptions,
) => api.useWorkspaceOperationsQuery({ scope: useWorkspaceScope(), ...params }, options);
export const useWorkspaceModelsQuery = () =>
  api.useWorkspaceModelsQuery({ scope: useWorkspaceScope() });
export const useWorkspaceMissionChecksQuery = (params: { id: string; before?: string }) =>
  api.useWorkspaceMissionChecksQuery({ scope: useWorkspaceScope(), ...params });
